/**
 * External Imports
 */
import { FC, useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import {
  NeuCheckbox
  // NeuFeedback,
} from '@neutron/react';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';
// import Loading from '../../shared/Loading';

import { RootState } from '../../../../redux/store';

import {
  useGetEmpDepartmentsQuery,
  useGetCSCDepartmentsByFacilityQuery,
  useUpdateCSCDepartmentsMappingMutation
} from '../../../../redux/apis/Admin.api';

/**
 * Global Type Definition Imports
 */
import {
  ICSCDepartmentMapping,
  EmpDepartment
} from '../../../../config/interfaces';

/**
 * Styled Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import { StyledLabelLoader } from './AreasTable.styles';

interface IDepartmentWithOpen extends EmpDepartment {
  isMapped: boolean;
  [key: string]: string | boolean;
}

interface IDepartmentsTableProps {
  selectedFacility: string;
  searchTerm: string;
}

const DepartmentsTable: FC<IDepartmentsTableProps> = ({
  selectedFacility,
  searchTerm
}) => {
  const [sortKey, setSortKey] = useState<string>('departmentId');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Department ID');
  const [isChangeMapFlagOnly, setIsChangeMapFlagOnly] =
    useState<boolean>(false);

  const {
    data: empDepartmentsData,
    // isError: isEmpDepartmentsError,
    // isFetching: isEmpDepartmentsFetching,
    isLoading: isDepartmentListLoading
  } = useGetEmpDepartmentsQuery(
    {
      facilityId: selectedFacility,
      onlyAdUserDepts: false
    },
    { skip: !selectedFacility }
  );

  const {
    data: cscMappedDepartmentsData,
    // isError: isCSCDepartmentsError,
    // isFetching: isCSCDepartmentsFetching,
    isLoading: isCSCDepartmentListLoading
  } = useGetCSCDepartmentsByFacilityQuery(
    { processLevelId: empDepartmentsData?.processLevel ?? '' },
    { skip: !empDepartmentsData }
  ) as { data: ICSCDepartmentMapping | undefined; isLoading: boolean };

  const [
    updateCSCDepartmentsMappingUrl
    // {
    //   isError: isDepartmentMappingListError,
    //   isLoading: isDepartmentMappingListLoading,
    //   isSuccess: isDepartmentMappingListSuccess
    // }
  ] = useUpdateCSCDepartmentsMappingMutation();

  useEffect(() => {
    if (isChangeMapFlagOnly) {
      setIsChangeMapFlagOnly(false);
    }
  }, [selectedFacility]);

  const handleSort = (column: string) => {
    if (isChangeMapFlagOnly) {
      setIsChangeMapFlagOnly(false);
    }
    let key;
    switch (column) {
      case 'Department ID':
        key = 'departmentId';
        break;
      case 'Department Name':
        key = 'departmentName';
        break;
      case 'Is Mapped':
        key = 'isMapped';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const handleChangeFlag = async (
    checked: boolean,
    department: IDepartmentWithOpen
  ) => {
    const { departmentId } = department;
    const currentMappedIds = cscMappedDepartmentsData?.departmentIds ?? [];
    const updatedDepartmentIds = checked
      ? [...currentMappedIds, departmentId]
      : currentMappedIds.filter(id => id !== departmentId);

    const postData = {
      id: empDepartmentsData?.processLevel,
      departmentIds: updatedDepartmentIds
    };
    await updateCSCDepartmentsMappingUrl(postData as any);
  };

  const combinedDepartmentsWithMappings = useMemo(() => {
    let allDepartments = (empDepartmentsData?.departments ?? []).map(
      (department: EmpDepartment) => ({
        ...department,
        isMapped:
          cscMappedDepartmentsData?.departmentIds?.includes(
            department.departmentId
          ) ?? false
      })
    );
    if (searchTerm) {
      allDepartments = allDepartments.filter(department => {
        return (
          department.departmentId
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          department.departmentName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      });
    }
    return allDepartments;
  }, [empDepartmentsData, cscMappedDepartmentsData, searchTerm]);

  const sortedDepartments = useMemo(() => {
    const stateCopy = [...combinedDepartmentsWithMappings];

    const sorted = stateCopy.sort(
      (a: IDepartmentWithOpen, b: IDepartmentWithOpen) => {
        if (a[sortKey] === b[sortKey]) {
          return 0;
        }
        if (!a[sortKey]) {
          return sortDir === 'asc' ? 1 : -1;
        }
        if (!b[sortKey]) {
          return sortDir === 'asc' ? -1 : 1;
        }
        if (sortDir === 'asc') {
          return a[sortKey]! > b[sortKey]! ? 1 : -1;
        }
        return a[sortKey]! > b[sortKey]! ? -1 : 1;
      }
    );
    return sorted;
  }, [combinedDepartmentsWithMappings, sortDir, sortKey]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Department ID"
      id="Departments-Table-Department-Id-Column"
      unit="column"
      colWidth={33}
      direction={!(active === 'Department ID') ? 'none' : sortDir}
      sort
    >
      Department ID
    </CellContainer>,
    <CellContainer
      key="Department Name"
      id="Departments-Table-Department-Name-Column"
      unit="column"
      colWidth={34}
      direction={!(active === 'Department Name') ? 'none' : sortDir}
      sort
    >
      Department Name
    </CellContainer>,
    <CellContainer
      key="Is Mapped"
      id="Departments-Table-Is-Mapped-Column"
      unit="column"
      colWidth={33}
      direction={!(active === 'Is Mapped') ? 'none' : sortDir}
      sort
    >
      Is Mapped
    </CellContainer>
  ];

  const rows = sortedDepartments.map((department: IDepartmentWithOpen) => (
    <StyledNeuTableRow
      key={`Department-Row-${department.departmentId}`}
      id={`Department-Row-${department.departmentId}`}
      unit="row"
      size="default"
      height="5em"
    >
      <CellContainer unit="column" colWidth={33}>
        <StyledLabel>{department.departmentId}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={34}>
        <StyledLabel>{department.departmentName}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={33}>
        <StyledLabelLoader>
          <NeuCheckbox
            checked={department.isMapped}
            color="blue"
            label="Is Mapped"
            onNeuChange={e => {
              setIsChangeMapFlagOnly(true);
              handleChangeFlag(e.detail.checked as boolean, department);
            }}
          />
        </StyledLabelLoader>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      customClass="admin-areas-unit-table"
      headers={headers}
      rows={rows}
      loading={isDepartmentListLoading || isCSCDepartmentListLoading}
      type="areas-units"
      onSort={(e: any) => handleSort(e.detail.label)}
      isAdminAreaUpdatesOnly={isChangeMapFlagOnly}
    />
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    selectedFacility: state.AdminReducer.selectedFacility
  };
};

export default connect(mapReduxStateToProps)(DepartmentsTable);
